import React, { useState, useEffect, useRef, useMemo } from 'react';
import styles from './index.module.scss';
import Toast from '@components/Toast';
import { setPostImage, commonImage } from '@images/imageUrls';
import { BASE_IMG_URL } from '@images/imageUrls';

interface Topic {
    id: string;
    name: string;
    cover: string;
    count: string;
    category?: string;
    // 统计字段（可选）
    threadCount?: number;
    thread_count?: number;
    likeCount?: number;
    like_count?: number;
    postCount?: number;
    post_count?: number;
    shareCount?: number;
    share_count?: number;
}

interface TopicPickerH5Props {
    visible: boolean;
    onClose: () => void;
    onSelect: (topics: Topic[]) => void;
    topicList?: Topic[];
    categoryList?: string[];
    onCategoryChange?: (categoryName: string, page?: number) => void;
    selectedTopicIds?: Array<string | number>;
    topicStore?: any; // 添加topicStore作为备用搜索方案
    initialSelected?: Topic[];
    hasMore?: boolean; // 是否还有更多数据（后端分页）
    isLoadingMore?: boolean; // 是否正在加载更多
    topicCache?: Record<string, Topic>; // 话题缓存，含分类信息
}


const TopicPickerH5: React.FC<TopicPickerH5Props> = ({
    visible,
    onClose,
    onSelect,
    topicList = [],
    categoryList = [],
    onCategoryChange,
    selectedTopicIds: selectedTopicIdsProp,
    initialSelected = [],
    topicStore,
    hasMore: hasMoreProp = true,
    isLoadingMore: isLoadingMoreProp = false,
    topicCache = {},
}) => {
    console.log('[TopicPickerH5] props snapshot', {
        visible,
        selectedTopicIdsProp,
        initialSelectedLength: initialSelected?.length,
        topicListLength: topicList?.length,
        categoryList,
        hasMoreProp,
        isLoadingMoreProp,
        topicCacheKeys: Object.keys(topicCache || {}),
    });
    const selectedTopicIds = useMemo(() => selectedTopicIdsProp ?? initialSelected.map(t => t.id), [selectedTopicIdsProp, initialSelected]);

    const [activeCategory, setActiveCategory] = useState(categoryList[0] || '');
    const [searchText, setSearchText] = useState('');
    const [selectedTopics, setSelectedTopics] = useState<Topic[]>(() => initialSelected.slice(0, 3));
    const [searchResults, setSearchResults] = useState<Topic[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    // 分页相关状态 - 使用后端分页
    const [page, setPage] = useState(1);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(hasMoreProp);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    // 记录上一次的 selectedTopicIds，用于判断是否需要重新同步
    const prevSelectedTopicIdsRef = useRef<string>('');
    // 标记是否已经初始化过
    const initializedRef = useRef(false);

    // 只在 selectedTopicIds 真正变化时同步，topicList 变化时只升级 placeholder
    useEffect(() => {
        console.log('[TopicPickerH5] sync effect triggered', {
            selectedTopicIds,
            topicListLength: topicList?.length,
            currentSelectedCount: selectedTopics.length,
            activeCategory,
            categoryList,
        });

        const externalIds = (selectedTopicIds || []).map(id => String(id)).sort().join(',');
        const prevExternalIds = prevSelectedTopicIdsRef.current;
        const fallbackCategory = activeCategory || categoryList[0] || '默认';

        // 如果 selectedTopicIds 没有变化，只尝试升级 placeholder
        if (prevExternalIds === externalIds && initializedRef.current) {
            // topicList 变化时，只升级 placeholder，不重置 selectedTopics
            const updatedTopics = selectedTopics.map(selected => {
                const stringId = String(selected.id);
                if (selected.name.startsWith('话题 ')) {
                    const realTopic = topicList?.find(t => String(t.id) === stringId);
                    if (realTopic) {
                        console.log('[TopicPickerH5] upgrading placeholder to real topic:', realTopic);
                        return realTopic;
                    }
                }
                return selected;
            });

            const hasUpgrade = updatedTopics.some((t, i) => t !== selectedTopics[i]);
            if (hasUpgrade) {
                setSelectedTopics(updatedTopics);
            }
            return;
        }

        // selectedTopicIds 变化了，需要重新同步
        prevSelectedTopicIdsRef.current = externalIds;
        initializedRef.current = true;

        if (!selectedTopicIds || selectedTopicIds.length === 0) {
            setSelectedTopics([]);
            return;
        }

        console.log('[TopicPickerH5] IDs changed, re-syncing. Prev:', prevExternalIds, 'Current:', externalIds);

        const preset: Topic[] = [];
        const foundIds = new Set<string>();

        selectedTopicIds.forEach(searchId => {
            const stringSearchId = String(searchId);

            // 先尝试从 topicCache 中取（含分类信息）
            if (topicCache && topicCache[stringSearchId]) {
                const cached = topicCache[stringSearchId];
                preset.push(cached);
                foundIds.add(stringSearchId);
                console.log('[TopicPickerH5] matched topic from cache:', cached);
                return;
            }

            const existingTopic = selectedTopics.find(t => String(t.id) === stringSearchId);
            if (existingTopic && existingTopic.name && !existingTopic.name.startsWith('话题 ')) {
                preset.push(existingTopic);
                foundIds.add(stringSearchId);
                console.log(`[TopicPickerH5] preserved existing topic:`, existingTopic);
                return;
            }

            const found = topicList?.find(t => String(t.id) === stringSearchId);
            if (found) {
                preset.push(found);
                foundIds.add(stringSearchId);
                console.log(`[TopicPickerH5] matched topic from list:`, found);
            }
        });

        selectedTopicIds.forEach(searchId => {
            if (!foundIds.has(String(searchId))) {
                const placeholder: Topic = {
                    id: String(searchId),
                    name: `话题 ${searchId}`,
                    cover: commonImage.official2,
                    count: '加载中...',
                    category: fallbackCategory,
                };
                preset.push(placeholder);
                console.log(`[TopicPickerH5] created placeholder for id:`, searchId, 'with fallbackCategory:', fallbackCategory);
            }
        });

        console.log('[TopicPickerH5] final preset:', preset);
        setSelectedTopics(preset);
    }, [selectedTopicIds, topicList, activeCategory, categoryList]);

    // 初始选中（仅在未显式传入 selectedTopicIds 时使用）
    useEffect(() => {
        if (selectedTopicIdsProp !== undefined) return;
        if (initialSelected && initialSelected.length >= 0) {
            const nextSelected = initialSelected.slice(0, 3);
            setSelectedTopics(nextSelected);
            prevSelectedTopicIdsRef.current = nextSelected.map(t => String(t.id)).sort().join(',');
            initializedRef.current = true;
        }
    }, [initialSelected, selectedTopicIdsProp]);

    // H5版防抖搜索函数
    const debouncedSearch = useRef((searchKeyword: string) => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(async () => {
            if (!searchKeyword.trim()) {
                setSearchResults([]);
                setHasSearched(false);
                setIsSearching(false);
                return;
            }

            setIsSearching(true);
            try {
                let response;

                // 方案一：尝试通过props传入的topicStore搜索
                if (topicStore && topicStore.getTopicList) {
                    response = await topicStore.getTopicList({
                        'filter[name]': searchKeyword.trim(),
                        'page[number]': 1,
                        'page[size]': 50
                    });
                } else {
                    // 方案二：动态导入topic相关API
                    const { default: topicAction } = await import('@stores/topic/action');
                    const topicInstance = topicAction;

                    // 调用搜索API
                    response = await topicInstance.getTopicList({
                        'filter[name]': searchKeyword.trim(),
                        'page[number]': 1,
                        'page[size]': 50
                    });
                }

                if (response && (response.code === 0 || response.Code === 0)) {
                    const topics = response.data?.data || response.Data?.data || [];
                    const formattedTopics = topics.map((topic: any, idx: number) => {
                        if (idx === 0) {
                            console.log('[TopicPickerH5] Search API topic structure:', topic);
                            console.log('[TopicPickerH5] Search API available keys:', Object.keys(topic));
                        }
                        // 尝试多种字段名获取统计数据
                        const threadCount = topic.threadCount || topic.thread_count || topic.postCount || topic.post_count || parseInt(topic.count) || 0;
                        const likeCount = topic.likeCount || topic.like_count || 0;
                        const postCount = topic.postCount || topic.post_count || 0;
                        const shareCount = topic.shareCount || topic.share_count || 0;

                        console.log(`[TopicPickerH5] Search result ${topic.name} - Original data:`, {
                            threadCount: topic.threadCount,
                            thread_count: topic.thread_count,
                            postCount: topic.postCount,
                            post_count: topic.post_count,
                            count: topic.count,
                            finalThreadCount: threadCount
                        });

                        return {
                            id: String(topic.id),
                            name: topic.name,
                            cover: topic.avatar || commonImage.official2,
                            count: `${threadCount}帖子 ${likeCount + postCount + shareCount}讨论量`,
                            category: topic.category?.name,
                            isHot: topic.isHot,
                            isNew: topic.isNew,
                            isRecommended: topic.isRecommended,
                        };
                    });
                    setSearchResults(formattedTopics);
                    setHasSearched(true);
                    console.log('[TopicPickerH5] Search results:', formattedTopics.length);
                } else {
                    setSearchResults([]);
                    setHasSearched(true);
                    console.warn('[TopicPickerH5] Search API failed:', response);
                }
            } catch (error) {
                console.error('[TopicPickerH5] Search error:', error);
                setSearchResults([]);
                setHasSearched(true);
            } finally {
                setIsSearching(false);
            }
        }, 300); // 300ms防抖
    }).current;

    // H5搜索文本变化处理
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchText(value);
        debouncedSearch(value);
    };

    // 清理定时器
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!visible) {
            setSearchText('');
            setSearchResults([]);
            setHasSearched(false);
            setIsSearching(false);
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        }
    }, [visible]);

    useEffect(() => {
        if (categoryList.length > 0) {
            if (!activeCategory || !categoryList.includes(activeCategory)) {
                console.log('[TopicPickerH5] setting activeCategory to first:', categoryList[0]);
                setActiveCategory(categoryList[0]);
            }
        }
    }, [categoryList]);

    const topicsByCategory = useMemo(() => {
        const map: { [key: string]: Topic[] } = {};
        topicList.forEach(topic => {
            const cat = (topic as any).category || '默认';
            if (!map[cat]) map[cat] = [];
            map[cat].push(topic);
        });
        return map;
    }, [topicList]);

    console.log('[TopicPickerH5] render - activeCategory:', activeCategory);
    console.log('[TopicPickerH5] render - topicList.length:', topicList.length);

    // 根据搜索状态决定显示的话题列表（memoized）
    // 现在 topicList 已经是当前分类的话题，不需要再按分类过滤
    const filteredTopics = useMemo(() => {
        const rawTopics = searchText ? searchResults : topicList.filter(topic => {
            const topicCategory = (topic as any).category || '默认';
            return topicCategory === activeCategory;
        });

        // 去重，避免相同 id 多次追加导致 key 冲突
        const uniqueTopicsMap = new Map<string, Topic>();
        rawTopics.forEach(topic => {
            const id = String(topic.id);
            if (!uniqueTopicsMap.has(id)) {
                uniqueTopicsMap.set(id, topic as Topic);
            }
        });
        const uniqueTopics = Array.from(uniqueTopicsMap.values());

        // 对非搜索结果的话题数据进行格式化处理
        if (searchText) return uniqueTopics;

        return uniqueTopics.map((topic, index) => {
            // 调试信息：查看实际数据结构
            if (index === 0) {
                console.log('[TopicPickerH5] topic data structure:', topic);
                console.log('[TopicPickerH5] available keys:', Object.keys(topic));
                console.log('[TopicPickerH5] Raw values:', {
                    threadCount: topic.threadCount,
                    thread_count: topic.thread_count,
                    postCount: topic.postCount,
                    post_count: topic.post_count,
                    count: topic.count,
                    likeCount: topic.likeCount,
                    like_count: topic.like_count
                });
            }

            // 尝试从多个可能的字段获取数据
            const threadCount = topic.threadCount || topic.thread_count || topic.postCount || topic.post_count || parseInt(String(topic.count).replace(/[^0-9]/g, '')) || 0;
            const likeCount = topic.likeCount || topic.like_count || 0;
            const postCount = topic.postCount || topic.post_count || 0;
            const shareCount = topic.shareCount || topic.share_count || 0;

            console.log(`[TopicPickerH5] ${topic.name} - Final values: threadCount: ${threadCount}, likeCount: ${likeCount}, postCount: ${postCount}, shareCount: ${shareCount}`);

            return {
                ...topic,
                count: topic.count && typeof topic.count === 'string' && topic.count.includes('讨论量')
                    ? topic.count
                    : `${threadCount}帖子 ${likeCount + postCount + shareCount}讨论量`
            };
        });
    }, [searchText, searchResults, topicList, activeCategory]);

    console.log('[TopicPickerH5] render - filteredTopics.length:', filteredTopics.length);
    if (filteredTopics.length > 0) {
        console.log('[TopicPickerH5] render - first 3 topics:', filteredTopics.slice(0, 3).map(t => `${t.name}(${t.category})`));
    }

    // 同步外部传入的 hasMore 和 isLoadingMore 状态
    useEffect(() => {
        setHasMore(hasMoreProp);
    }, [hasMoreProp]);

    useEffect(() => {
        setIsLoadingMore(isLoadingMoreProp);
    }, [isLoadingMoreProp]);

    // 数据回填完成后重置加载态，避免第一次进入无法继续翻页
    useEffect(() => {
        if (!visible) return;
        setIsLoadingMore(false);
    }, [topicList, visible]);

    // 分页：当分类变化时重置分页状态
    useEffect(() => {
        // 如果是搜索模式，不需要分页
        if (searchText) {
            setPage(1);
            return;
        }
        // 分类变化时重置页码
        setPage(1);
        setHasMore(hasMoreProp);
    }, [searchText, activeCategory]);

    // 后端分页：加载更多时调用 onCategoryChange 请求下一页
    const loadMore = () => {
        if (isLoadingMore || !hasMore || searchText) return;
        const nextPage = page + 1;
        setPage(nextPage);
        setIsLoadingMore(true);
        if (onCategoryChange) {
            console.log('[TopicPickerH5] loadMore - calling API for page:', nextPage);
            onCategoryChange(activeCategory, nextPage);
        }
    };

    // 列表容器 ref，用于监听滚动触底
    const listRef = useRef<HTMLDivElement | null>(null);
    // 节流标记
    const scrollThrottleRef = useRef<number | null>(null);

    // 滚动处理：触底时加载下一页
    const handleScrollCore = (el: HTMLDivElement | null) => {
        if (searchText) return; // 搜索模式不触发客户端分页
        if (isLoadingMore || !hasMore) return;
        if (!el) return;
        const threshold = 80; // 距离底部多少像素触发
        if (el.scrollHeight - el.scrollTop - el.clientHeight <= threshold) {
            if (scrollThrottleRef.current) return;
            // 节流 200ms
            scrollThrottleRef.current = window.setTimeout(() => {
                scrollThrottleRef.current && window.clearTimeout(scrollThrottleRef.current as number);
                scrollThrottleRef.current = null;
            }, 200);
            loadMore();
        }
    };

    const handleScrollReact: React.UIEventHandler<HTMLDivElement> = (e) => handleScrollCore(e.currentTarget);

    // 在所有 hooks 之后做早期返回，保证 hooks 顺序一致
    if (!visible) return null;

    const getCategorySelectedCount = (category: string) => {
        const count = selectedTopics.filter(t => {
            const idStr = String(t.id);
            // 优先使用缓存里的分类
            if (topicCache && topicCache[idStr]?.category) {
                return topicCache[idStr].category === category;
            }
            if (t.category) return t.category === category;
            return category === activeCategory; // 无分类信息时归为当前分类显示计数
        }).length;
        console.log('[TopicPickerH5] badge calc', { category, activeCategory, count, selectedTopics, cacheHitIds: selectedTopics.filter(t => topicCache[String(t.id)]).map(t => t.id) });
        return count;
    };

    const handleToggleSelect = (topic: Topic) => {
        console.log('[TopicPickerH5] handleToggleSelect called with topic:', topic);
        const isSelected = selectedTopics.some(t => t.id === topic.id);
        console.log('[TopicPickerH5] isSelected:', isSelected, 'currentSelected:', selectedTopics);
        if (isSelected) {
            const newSelected = selectedTopics.filter(t => t.id !== topic.id);
            console.log('[TopicPickerH5] removing topic, newSelected:', newSelected);
            setSelectedTopics(newSelected);
        } else {
            if (selectedTopics.length >= 3) {
                Toast.info('最多添加3个话题');
                return;
            }
            const newSelected = [...selectedTopics, topic];
            console.log('[TopicPickerH5] adding topic, newSelected:', newSelected);
            setSelectedTopics(newSelected);
        }
    };

    const handleConfirm = () => {
        console.log('[TopicPickerH5] handleConfirm called with selectedTopics:', selectedTopics);
        onSelect(selectedTopics);
        onClose();
    };

    const handleCancel = () => {
        onClose();
    };
    // 话题标签
    const tagRender = (item) => {
        const tags: any = [];

        // 规则 只显示一个标签，优先级为热>新>荐
        if (item.isHot) {
            tags.push({ img: `${BASE_IMG_URL}/common/topic_hot.png`, alt: '热' });
        } else if (item.isNew) {
            tags.push({ img: `${BASE_IMG_URL}/common/topic_new.png`, alt: '新' });
        } else if (item.isRecommended) {
            tags.push({ img: `${BASE_IMG_URL}/common/topic_recommend.png`, alt: '荐' });
        }

        
        if (tags.length === 0) {
            return null;
        }

        return (
            <>
                {tags.map((tag, index) => (
                    <img
                        key={index}
                        src={tag.img}
                        alt={tag.alt}
                        className={styles.topicTag}
                        title={tag.alt}
                    />
                ))}
            </>
        );
    };
    return (
        <>
            {/* 遮罩层 */}
            <div className={styles.mask} onClick={onClose}></div>

            {/* 弹窗内容 */}
            <div className={styles.topicPickerWrap}>
                {/* 标题区域 */}
                <div className={styles.header}>
                    <div className={styles.title}>选择话题</div>
                    <div className={styles.subtitle}>最多添加3个话题</div>
                </div>

                {/* 搜索框 */}
                <div className={styles.searchBar}>
                    <div className={styles.searchBox}>
                        <img
                            src={setPostImage.searchSetpost}
                            alt="search"
                            className={styles.searchIcon}
                        />
                        <input
                            type="text"
                            placeholder="搜索话题"
                            className={styles.searchInput}
                            value={searchText}
                            onChange={handleSearchChange}
                        />
                    </div>
                </div>

                {/* 内容区域 */}
                <div className={styles.contentArea}>
                    {/* 左侧分类 - 搜索时隐藏 */}
                    {!searchText && (
                        <div className={styles.categoryList}>
                            {(categoryList.length ? categoryList : Object.keys(topicsByCategory)).map(category => {
                                const selectedCount = getCategorySelectedCount(category);
                                return (
                                    <div
                                        key={category}
                                        className={`${styles.categoryItem} ${activeCategory === category ? styles.categoryItemActive : ''}`}
                                        onClick={() => {
                                            console.log('[TopicPickerH5] category click:', category);
                                            setActiveCategory(category);
                                            setPage(1);
                                            setHasMore(true);
                                            if (listRef.current) {
                                                listRef.current.scrollTop = 0;
                                            }
                                            if (onCategoryChange) {
                                                console.log('[TopicPickerH5] calling onCategoryChange:', category, 'page: 1');
                                                onCategoryChange(category, 1);
                                            }
                                        }}
                                    >
                                        <span className={styles.categoryName}>{category}</span>
                                        {selectedCount > 0 && (
                                            <span className={styles.categoryBadge}>{selectedCount}</span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* 右侧话题列表（使用后端分页） */}
                    <div
                        ref={listRef}
                        className={`${styles.topicList} ${searchText ? styles.topicListFull : ''}`}
                        onScroll={handleScrollReact}
                    >
                        {(searchText ? searchResults : filteredTopics).length === 0 && searchText ? (
                            <div className={styles.emptyState}>
                                <img
                                    src={setPostImage.emptyIcon}
                                    alt="empty"
                                    className={styles.emptyIcon}
                                />
                                <div className={styles.emptyText}>未搜索到相关话题</div>
                            </div>
                        ) : (searchText ? searchResults : filteredTopics).length === 0 ? (
                            <div className={styles.emptyState}>
                                <div className={styles.emptyText}>加载中...</div>
                            </div>
                        ) : (
                            (searchText ? searchResults : filteredTopics).map((topic, idx) => {
                                const isSelected = selectedTopics.some(t => t.id === topic.id);
                                return (
                                    <div
                                        key={`${topic.id}-${topic.category || activeCategory || ''}-${idx}`}
                                        className={styles.topicItem}
                                        onClick={() => handleToggleSelect(topic)}
                                    >
                                        <div className={styles.topicInfo}>
                                            <img src={topic.cover} alt={topic.name} className={styles.topicCover} />
                                            <div className={styles.topicContent}>
                                                <div className={styles.topicName}>
                                                    <span className={styles.topicText}>{topic.name}</span>
                                                    {tagRender(topic)}
                                                </div>
                                                <div className={styles.topicCount}>{topic.count}</div>
                                            </div>
                                        </div>
                                        <img
                                            src={isSelected
                                                ? setPostImage.atCheckoutActive
                                                : setPostImage.atCheckout
                                            }
                                            alt="check"
                                            className={styles.checkIcon}
                                        />
                                    </div>
                                );
                            })
                        )}

                        {!searchText && (
                            <div className={styles.listFooter}>
                                {isLoadingMore ? (
                                    <div className={styles.footerText}>加载中...</div>
                                ) : hasMore ? (
                                    <div className={styles.footerText} onClick={loadMore}>继续加载</div>
                                ) : (
                                    <div className={styles.footerText}>没有更多了</div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* 底部按钮 */}
                <div className={styles.bottomBar}>
                    <button className={styles.cancelBtn} onClick={handleCancel}>
                        取消
                    </button>
                    <button
                        className={`${styles.confirmBtn} ${selectedTopics.length > 0 ? styles.confirmBtnActive : ''}`}
                        onClick={handleConfirm}
                        disabled={selectedTopics.length === 0}
                    >
                        确定 {selectedTopics.length > 0 && <>({selectedTopics.length})</>}
                    </button>
                </div>
            </div>
        </>
    );
};

export default TopicPickerH5;
